import { LocationNudge } from "./LocationNudge";
import { usePinnedBottom } from "../hooks/usePinnedBottom";
import React, { useState, useMemo, useEffect, Suspense } from "react";
import { lazyWithRetry } from "../lib/lazyWithRetry";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shirt, Home, MessageCircle, Users2, UsersRound, User, LogOut, Shield, PawPrint, Menu, X, Target, Moon, ScrollText, Trophy, Flame, Sparkles, ShoppingBag, Film, Globe, BookOpen, MessageSquarePlus, ShieldAlert, LifeBuoy, GraduationCap } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import { useSafetyAlerts } from "../context/SafetyAlertContext";
import { LOGO, api } from "../lib/api";
import { Avatar } from "./Avatar";
import { RankBadge } from "./RankBadge";
import { VerifiedBadge } from "./VerifiedBadge";
import { BirthdayBadge } from "./BirthdayBadge";
import { nameColorClass } from "../lib/cosmetics";
import { GlobalSearch } from "./layout/GlobalSearch";
import { PresenceSwitcher } from "./layout/PresenceSwitcher";
import { NotificationBell } from "./layout/NotificationBell";
import { VisitorsBell } from "./layout/VisitorsBell";
import { MoonPhaseWidget } from "./layout/MoonPhaseWidget";
import { FreezeBanner } from "./layout/FreezeBanner";
import { SessionExpiryWarning } from "./layout/SessionExpiryWarning";
import { PasswordBreachBanner } from "./layout/PasswordBreachBanner";
import { WarningBanner } from "./layout/WarningBanner";
import { AccountMenu } from "./layout/AccountMenu";
import { StatusBanner, MaintenanceLock, useSiteStatus } from "./StatusBanner";
import { presenceMeta } from "../lib/presence";
import { SiteFooter, SkipLink } from "./layout/SiteFooter";

// Item 10 — rarely-needed chrome is code-split out of the main bundle.
const InstallPrompt = lazyWithRetry(() => import("./layout/InstallPrompt").then((m) => ({ default: m.InstallPrompt })));
const PushPrompt = lazyWithRetry(() => import("./layout/PushPrompt").then((m) => ({ default: m.PushPrompt })));
const AlphaAttentionStrip = lazyWithRetry(() => import("./layout/AlphaAttentionStrip").then((m) => ({ default: m.AlphaAttentionStrip })));

const NAV = [
  { to: "/", label: "Howl Feed", k: "nav.feed", icon: Home, testid: "nav-feed-link", end: true },
  { to: "/messages", label: "Messages", k: "nav.messages", icon: MessageCircle, testid: "nav-messages-link" },
  { to: "/dens", label: "Pack Dens", k: "nav.dens", icon: Users2, testid: "nav-group-chats-link" },
  { to: "/events", label: "Full Moon Events", k: "nav.events", icon: Moon, testid: "nav-events-link" },
  { to: "/mates", label: "Pack Mates", k: "nav.mates", icon: UsersRound, testid: "nav-mates-link" },
  { to: "/quests", label: "Quests", k: "nav.quests", icon: Target, testid: "nav-quests-link" },
  { to: "/shop", label: "Howl Market", k: "nav.shop", icon: ShoppingBag, testid: "nav-shop-link" },
  { to: "/merch", label: "Pack Merch", k: "nav.merch", icon: Shirt, testid: "nav-merch-link" },
  { to: "/cinema", label: "Pack Cinema", k: "nav.cinema", icon: Film, testid: "nav-cinema-link" },
  { to: "/blog", label: "Pack Blog", k: "nav.blog", icon: BookOpen, testid: "nav-blog-link" },
  { to: "/map", label: "Pack Map", k: "nav.map", icon: Globe, testid: "nav-map-link" },
  { to: "/recruiters", label: "Pack Recruiters", k: "nav.recruiters", icon: Trophy, testid: "nav-recruiters-link" },
  { to: "/profile", label: "My Den", k: "nav.profile", icon: User, testid: "nav-profile-link" },
  { to: "/rules", label: "Code of the Pack", k: "nav.code", icon: ScrollText, testid: "nav-rules-link" },
  { to: "/helpdesk", label: "Helpdesk", k: "nav.helpdesk", icon: LifeBuoy, testid: "nav-helpdesk-link" },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { connected, notifications, unread, markNotificationsRead, myPresence, myStatus, setPresence, dmUnread, denUnread, helpdeskUnread } = useSocket();
  const { affectsMeCount: safetyCount } = useSafetyAlerts();  // only members inside an alert area see the Safety Alert button
  const navigate = useNavigate();
  const [bellOpen, setBellOpen] = useState(false);
  const [safetyOpen, setSafetyOpen] = useState(false);
  const [visitorsOpen, setVisitorsOpen] = useState(false);
  const [presenceOpen, setPresenceOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const bottomNavRef = usePinnedBottom();
  const [academyOn, setAcademyOn] = useState(false);
  useEffect(() => {
    let stop = false;
    api.get("/academy/overview").then((r) => { if (!stop) setAcademyOn(!!r.data?.enabled); }).catch(() => {});
    return () => { stop = true; };
  }, [user && user.id]);
  const badgeFor = (to) => (to === "/messages" ? dmUnread : to === "/dens" ? denUnread : to === "/helpdesk" ? helpdeskUnread : 0);
  const status = useSiteStatus();
  const locked = status?.active && status?.level === "maintenance" && !user?.is_admin;

  const navItems = useMemo(() => {
    const items = [...NAV];
    if (academyOn) {
      const idx = items.findIndex((n) => n.to === "/recruiters");
      const link = { to: "/academy", label: "Pack University", k: "nav.academy", icon: GraduationCap, testid: "nav-academy-link" };
      if (idx >= 0) items.splice(idx + 1, 0, link); else items.push(link);
    }
    if (user?.is_admin) items.push({ to: "/admin", label: "Alpha Console", k: "nav.admin", icon: Shield, testid: "nav-admin-link" });
    if (safetyCount > 0) items.unshift({ to: "/safety", label: "Safety Alert", icon: ShieldAlert, testid: "nav-safety-link", danger: true });
    return items;
  }, [user, safetyCount, academyOn]);

  const bottomNavItems = useMemo(
    () => {
      const base = ["/", "/messages", "/dens", "/quests", "/mates"].map((to) => navItems.find((n) => n.to === to)).filter(Boolean);
      if (safetyCount > 0) {
        const s = navItems.find((n) => n.to === "/safety");
        if (s) return [s, ...base.slice(0, 4)];
      }
      return base;
    },
    [navItems, safetyCount]
  );

  if (locked) return <MaintenanceLock status={status} onLogout={logout} />;

  return (
    <div className="min-h-screen sanctuary-bg">
      <SkipLink />
      {/* Header */}
      <header data-testid="nav-header" className="fixed top-0 inset-x-0 z-50 glass border-b border-slate-800/60 pt-[var(--hdr-top)] transition-[padding] duration-200">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-6 h-16 flex items-center gap-3">
          <button className="lg:hidden text-slate-300" onClick={() => setMobileNav(!mobileNav)} data-testid="mobile-nav-toggle">
            {mobileNav ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <NavLink to="/" data-testid="nav-brand-logo" className="hidden sm:flex items-center gap-2 shrink-0">
            <img loading="lazy" decoding="async" src={LOGO} alt="The Packs Sanctuary" className="w-11 h-11 rounded-full ring-1 ring-slate-700 object-cover" />
            <div className="hidden sm:block leading-tight">
              <div className="font-display font-bold text-slate-100 text-sm tracking-wide">THE PACKS</div>
              <div className="font-display text-amber-400 text-xs tracking-[0.3em]">SANCTUARY</div>
            </div>
          </NavLink>

          <GlobalSearch />

          <div className="flex items-center gap-2 sm:gap-3 ml-auto">
            {user?.streak > 0 && (
              <NavLink
                to="/"
                data-testid="header-streak-chip"
                title={`${user.streak}-day howl streak`}
                className="hidden md:flex items-center gap-1 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-300 hover:bg-amber-500/20 transition-colors"
              >
                <Flame className="w-3.5 h-3.5" /> {user.streak}
              </NavLink>
            )}
            {user?.freeze_tokens > 0 && (
              <NavLink
                to="/"
                data-testid="header-freeze-badge"
                title="Streak freeze available — one missed day won't break your run"
                className="hidden md:flex items-center rounded-full border border-sky-500/30 bg-sky-500/10 px-2 py-1 text-xs font-semibold text-sky-300 hover:bg-sky-500/20 transition-colors"
              >
                <Shield className="w-3.5 h-3.5" />
              </NavLink>
            )}
            <PresenceSwitcher
              open={presenceOpen}
              onToggle={() => { setPresenceOpen(!presenceOpen); setBellOpen(false); setSafetyOpen(false); setVisitorsOpen(false); }}
              onClose={() => setPresenceOpen(false)}
              connected={connected}
              myPresence={myPresence}
              myStatus={myStatus}
              setPresence={setPresence}
            />

            {/* Sound settings */}
            <NotificationBell
              open={bellOpen}
              onToggle={() => { const n = !bellOpen; setBellOpen(n); setPresenceOpen(false); setSafetyOpen(false); setVisitorsOpen(false); if (n) markNotificationsRead(); }}
              onClose={() => setBellOpen(false)}
              notifications={notifications}
              unread={unread}
              navigate={navigate}
            />

            <VisitorsBell
              open={visitorsOpen}
              onToggle={() => { const n = !visitorsOpen; setVisitorsOpen(n); setBellOpen(false); setPresenceOpen(false); setSafetyOpen(false); }}
              onClose={() => setVisitorsOpen(false)}
            />

            <AccountMenu
              open={safetyOpen}
              onToggle={() => { const n = !safetyOpen; setSafetyOpen(n); setBellOpen(false); setPresenceOpen(false); setVisitorsOpen(false); }}
              onClose={() => setSafetyOpen(false)}
              myPresence={myPresence}
            />
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto pt-[calc(4rem+var(--hdr-top))] flex">
        {/* Left Sidebar */}
        <aside data-testid="left-sidebar" className={`${mobileNav ? "flex" : "hidden"} lg:flex fixed lg:sticky top-[calc(4rem+var(--hdr-top))] left-0 z-40 lg:z-auto h-[calc(100vh-4rem-var(--hdr-top))] w-64 shrink-0 border-r border-slate-800/60 glass lg:bg-transparent px-3 pt-2 pb-24 lg:pb-2 flex-col overflow-hidden`}>
          <nav aria-label="Main" className="flex-1 min-h-0 max-h-[760px] -mx-1 px-1 flex flex-col">
            {navItems.map((item) => (
              <React.Fragment key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                onClick={() => setMobileNav(false)}
                className={({ isActive }) =>
                  item.danger
                    ? `flex items-center gap-3 px-3 flex-1 basis-0 min-h-[23px] max-h-[38px] py-0 rounded-xl text-[13px] leading-[18px] [@media(max-height:780px)]:leading-4 [@media(max-height:780px)]:text-[12.5px] font-bold border safety-nav-flash ${isActive ? "ring-1 ring-red-400/60" : ""}`
                    : `flex items-center gap-3 px-3 flex-1 basis-0 min-h-[23px] max-h-[38px] py-0 rounded-xl text-[13px] leading-[18px] [@media(max-height:780px)]:leading-4 [@media(max-height:780px)]:text-[12.5px] font-medium transition-all hover-lift border border-transparent ${
                        isActive
                          ? "bg-sky-500/10 text-sky-300 border-sky-500/30 howl-glow"
                          : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/50"
                      }`
                }
              >
                <item.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                {item.k ? t(item.k, item.label) : item.label}
                {item.danger && safetyCount > 1 && (
                  <span data-testid="sidebar-safety-count" className="ml-auto min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">
                    {safetyCount}
                  </span>
                )}
                {badgeFor(item.to) > 0 && (
                  <span data-testid={`sidebar-badge-${item.testid}`} className="ml-auto min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">
                    {badgeFor(item.to) > 99 ? "99+" : badgeFor(item.to)}
                  </span>
                )}
              </NavLink>
              </React.Fragment>
            ))}
          </nav>

          <MoonPhaseWidget />

          <div className="mt-1.5 shrink-0 px-3 py-2 rounded-xl bg-gradient-to-br from-slate-900 to-sky-950/30 border border-slate-800">
            <div className="flex items-center gap-3">
              <Avatar user={user} size="md" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate flex items-center gap-1"><span className={nameColorClass(user?.equipped_name_color) || "text-slate-100"}>{user?.name}</span> <VerifiedBadge verified={user?.verified} className="w-3.5 h-3.5" /> <BirthdayBadge birthday={user?.is_birthday} className="w-3.5 h-3.5" /></div>
                <div className="text-xs text-slate-500 truncate">@{user?.username}</div>
              </div>
              <div className="flex items-center gap-1 leading-none shrink-0" title="Pack mates" data-testid="sidebar-mates-count">
                <UsersRound className="w-3.5 h-3.5 text-sky-400" />
                <span className="text-sm font-semibold text-slate-100">{user?.mates_count ?? 0}</span>
              </div>
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-2">
              {user && <RankBadge rank={user.rank} />}
              <div className="flex items-center gap-1 leading-none shrink-0" title="Howl Points" data-testid="sidebar-howl-points">
                <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-sm font-semibold text-slate-100">{(user?.points ?? 0).toLocaleString()}</span>
              </div>
            </div>
          </div>
          <div className="mt-1.5 shrink-0 text-center flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 text-[10.5px] leading-4">
            <NavLink to="/privacy" data-testid="sidebar-privacy-link" className="text-slate-500 hover:text-sky-400">Privacy Policy</NavLink>
            <span className="text-slate-700">·</span>
            <NavLink to="/terms" data-testid="sidebar-terms-link" className="text-slate-500 hover:text-sky-400">Terms of Use</NavLink>
            <span className="text-slate-700">·</span>
            <NavLink to="/cookies" data-testid="sidebar-cookie-policy-link" className="text-slate-500 hover:text-sky-400">Cookie Policy</NavLink>
            <span className="text-slate-700">·</span>
            <NavLink to="/accessibility" data-testid="sidebar-accessibility-link" className="text-slate-500 hover:text-sky-400">Accessibility Statement</NavLink>
            <span className="text-slate-700">·</span>
            <NavLink to="/ccpa-opt-out" data-testid="sidebar-ccpa-link" className="text-slate-500 hover:text-sky-400">Do Not Sell My Information</NavLink>
            <span className="text-slate-700">·</span>
            <NavLink to="/compliance" data-testid="sidebar-compliance-link" className="text-slate-500 hover:text-sky-400">Compliance</NavLink>
          </div>
        </aside>

        {mobileNav && <div className="lg:hidden fixed inset-0 top-[calc(4rem+var(--hdr-top))] bg-black/60 z-30" onClick={() => setMobileNav(false)} />}

        {/* Main content */}
        <main id="main-content" tabIndex={-1} className="flex-1 min-w-0 overflow-x-clip px-3 sm:px-6 py-6 pb-[calc(6rem+env(safe-area-inset-bottom))] lg:pb-6 outline-none">
          {user?.is_admin && <Suspense fallback={null}><AlphaAttentionStrip /></Suspense>}
          <StatusBanner status={status} />
          <WarningBanner />
          {children}
          <LocationNudge />
          <SiteFooter />
        </main>
      </div>

      {/* Mobile bottom navigation — always visible so the sidebar is reachable on phones */}
      <nav ref={bottomNavRef} data-testid="mobile-bottom-nav" className="lg:hidden fixed bottom-0 inset-x-0 z-50 glass border-t border-slate-800/60 flex items-stretch justify-around px-1 pb-[env(safe-area-inset-bottom)]">
        {bottomNavItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`bottom-${item.testid}`}
            className={({ isActive }) =>
              item.danger
                ? `flex flex-col items-center justify-center gap-0.5 flex-1 py-2 text-[10px] font-bold text-red-400 safety-nav-flash rounded-lg`
                : `flex flex-col items-center justify-center gap-0.5 flex-1 py-2 text-[10px] font-medium transition-colors ${
                    isActive ? "text-sky-400" : "text-slate-500 hover:text-slate-300"
                  }`
            }
          >
            <span className="relative">
              <item.icon className="w-5 h-5" />
              {item.danger && safetyCount > 1 && (
                <span data-testid="bottom-safety-count" className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center">
                  {safetyCount > 9 ? "9+" : safetyCount}
                </span>
              )}
              {badgeFor(item.to) > 0 && (
                <span data-testid={`bottom-badge-${item.testid}`} className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-red-600 text-white text-[9px] font-bold flex items-center justify-center">
                  {badgeFor(item.to) > 9 ? "9+" : badgeFor(item.to)}
                </span>
              )}
            </span>
            <span className="truncate max-w-[64px]">{item.k ? t(item.k.replace("nav.", "navShort."), { defaultValue: item.label.replace("Howl ", "").replace("Pack ", "").replace("Alpha ", "") }) : item.label}</span>
          </NavLink>
        ))}
      </nav>
      <Suspense fallback={null}><InstallPrompt /><PushPrompt /></Suspense>
      <FreezeBanner />
      <SessionExpiryWarning />
      <PasswordBreachBanner />
    </div>
  );
}
